import { createParser } from "nuqs";
import type { EntryFilterState, EnumFilterField } from "./types";

export const FILTER_FIELDS = [
	"status",
	"timekeeper",
	"application",
	"matter",
	"client",
	"billableCategory",
	"task",
	"activity",
] as const satisfies readonly EnumFilterField[];

const FIELD_SET = new Set<string>(FILTER_FIELDS);
const FIELD_SEP = ":";
const TOKEN_SEP = ";";
const VALUE_SEP = ",";

export function isFilterField(value: string): value is EnumFilterField {
	return FIELD_SET.has(value);
}

function safeDecode(value: string): string | null {
	try {
		return decodeURIComponent(value);
	} catch {
		return null;
	}
}

function valuesFrom(raw: string): string[] {
	if (raw.length === 0) return [];
	const out: string[] = [];
	for (const piece of raw.split(VALUE_SEP)) {
		if (piece.length === 0) continue;
		const decoded = safeDecode(piece);
		if (decoded != null) out.push(decoded);
	}
	return Array.from(new Set(out));
}

function parseNewToken(token: string): Partial<EntryFilterState> | null {
	const idx = token.indexOf(FIELD_SEP);
	if (idx < 0) return null;
	const field = token.slice(0, idx);
	const rawValues = token.slice(idx + 1);
	if (!isFilterField(field)) return null;
	const values = valuesFrom(rawValues);
	if (field === "timekeeper") return { timekeeper: values[0] ?? null };
	return { [field]: values } as Partial<EntryFilterState>;
}

// Lightweight migration for old chip URLs: e:<field>:is:<values>. Unsupported
// operators/text chips are ignored instead of crashing or preserving chip state.
function parseOldChip(token: string): Partial<EntryFilterState> | null {
	const parts = token.split(FIELD_SEP);
	if (parts.length < 4 || parts[0] !== "e") return null;
	const field = parts[1];
	const operator = parts[2];
	if (!isFilterField(field) || operator !== "is") return null;
	const values = valuesFrom(parts.slice(3).join(FIELD_SEP));
	if (field === "timekeeper") return { timekeeper: values[0] ?? null };
	return { [field]: values } as Partial<EntryFilterState>;
}

function parse(value: string): EntryFilterState {
	const next: Partial<EntryFilterState> = {};
	if (value.length === 0) return next as EntryFilterState;
	for (const token of value.split(TOKEN_SEP)) {
		if (!token) continue;
		const patch = parseNewToken(token) ?? parseOldChip(token);
		if (!patch) continue;
		Object.assign(next, patch);
	}
	return next as EntryFilterState;
}

export function serializeEntryFilters(filters: EntryFilterState): string {
	const tokens: string[] = [];
	for (const field of FILTER_FIELDS) {
		if (field === "timekeeper") {
			const value = filters.timekeeper;
			if (value)
				tokens.push(`${field}${FIELD_SEP}${encodeURIComponent(value)}`);
			continue;
		}
		const values = filters[field];
		if (!values) continue;
		tokens.push(
			`${field}${FIELD_SEP}${values.map(encodeURIComponent).join(VALUE_SEP)}`,
		);
	}
	return tokens.join(TOKEN_SEP);
}

export const filterStateParser = createParser<EntryFilterState>({
	parse,
	serialize: serializeEntryFilters,
	eq: (a, b) => serializeEntryFilters(a) === serializeEntryFilters(b),
});
